import React, { useEffect, useState } from 'react'

import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { useRouter } from '@shared/hooks/use-router'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material'
import { ErrorsAlert } from '@shared/components/errors-alert.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useResourceQuery } from '@shared/hooks/use-resource-query.hook'
import { useMutation, useQueryClient } from 'react-query'
import { apiJSON, NormalizedErrors } from '@shared/utils/api/api.util'
import capitalizeFirstLetter from '@shared/utils/capitalize-first-letter'

type RouteQuery = {
  resourceID?: string
  companyID?: string
}

export const AspectsDialog = ({
  companyID,
  onClose,
  afterCreate,
}: {
  companyID?: string
  onClose?: () => void
  afterCreate?: () => void
}) => {
  const classes = useThemeStyles()
  const queryClient = useQueryClient()
  const { navigate, query } = useRouter<RouteQuery>()
  const [error, setError] = useState<NormalizedErrors | null>(null)
  const isResourceEdit = Boolean(query?.resourceID)

  const formMethods = useForm<any>({
    defaultValues: { Name: '', IsSignificant: false, CompanyId: companyID },
  })
  const { reset: formMethodsReset } = formMethods

  const { resourceData, readResourceData, isResourceDataFetching } =
    useResourceQuery(`companies/${query?.companyID}/Aspects`, query?.resourceID)

  const {
    mutate: createResource,
    isLoading: isResourceCreating,
    isSuccess: isResourceCreationSucceed,
    error: createResourceErrors,
    reset: resetResourceCreationState,
  } = useMutation<unknown, NormalizedErrors, object>(
    (resourceData: object) =>
      apiJSON(
        `companies/${query?.companyID}/Aspects${isResourceEdit ? `/${query.resourceID || ''}` : ''}`,
        resourceData,
        {
          method: isResourceEdit ? 'PUT' : 'POST',
          headers: new Headers(),
        }
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries()
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes('crud'),
        })
      },
    }
  )

  const handleSubmit = formMethods.handleSubmit((resourceFormData) => {
    setError(null)

    resetResourceCreationState()
    const formData = new FormData()
    if (isResourceEdit) {
      formData.append('aspectId', query?.resourceID || '')
    }

    Object.keys(resourceFormData).forEach((key) => {
      formData.append(
        key,
        key === 'CompanyId' ? +resourceFormData[key] : resourceFormData[key]
      )
    })

    createResource(formData)
  })

  useEffect(() => {
    if (isResourceEdit) {
      readResourceData()
    }
  }, [isResourceEdit, readResourceData])

  useEffect(() => {
    if (isResourceEdit && resourceData) {
      setTimeout(() => {
        formMethodsReset(
          Object.keys(resourceData).reduce(
            (acc: Record<string, any>, key: string) => {
              acc[capitalizeFirstLetter(key)] = resourceData[key]
              return acc
            },
            {}
          )
        )
      }, 10)
    }
  }, [isResourceEdit, resourceData, formMethodsReset])

  useEffect(() => {
    formMethods.register('companyId', { required: true, value: companyID })
  }, [companyID])

  useEffect(() => {
    if (isResourceCreationSucceed) {
      if (afterCreate) {
        return afterCreate()
      }
      navigate(-1)
    }
  }, [afterCreate, isResourceCreationSucceed])

  return (
    <Dialog fullWidth open={true} maxWidth="md">
      <form noValidate>
        <FormProvider {...formMethods}>
          <DialogTitle className={classes.dialogTitle}>
            {isResourceEdit ? 'Edytuj' : 'Dodaj nowy aspekt'}
          </DialogTitle>
          <DialogContent dividers>
            <ErrorsAlert
              errors={createResourceErrors || error}
              onClose={resetResourceCreationState}
            />
            <TextField
              {...formMethods.register('Name', {
                required: true,
              })}
              required={true}
              key="Name"
              label="Wyświetlana nazwa aspektu"
              fullWidth
              multiline={true}
              rows={10}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
            <Controller
              control={formMethods.control}
              name="IsSignificant"
              defaultValue={false}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label="Aspekt znaczący"
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => (onClose ? onClose() : navigate(-1))}
              color="primary"
              variant="outlined"
            >
              Anuluj
            </Button>
            <ProgressButton
              onClick={handleSubmit}
              isLoading={isResourceCreating}
              color="primary"
              variant="contained"
            >
              Zapisz
            </ProgressButton>
          </DialogActions>
        </FormProvider>
      </form>
    </Dialog>
  )
}
